import { compose, createStore, applyMiddleware, combineReducers } from "redux";
import { Map } from "immutable";
import thunk from "redux-thunk";
import { createBrowserHistory } from "history";
import {
  connectRouter,
  routerMiddleware,
  push,
  replace,
} from "connected-react-router";

import api from "../services/api";
import utility from "../services/utility";
import view from "../services/view";
import {
  COLOR_FACET_ID,
  CATEGORY_FACET_ID,
  CLOSE_MENU_PSEUDO_FACET_ID,
  PRICE_PSEUDO_FACET_ID,
  PRIORITIZE_THIS_JOB,
  RESET_CATEGORY_JOB,
} from "../constants";

const initialServerData = window.store_filters_initial_props || {};
const initialState = Map({
  name: initialServerData.name,
  nbHits: initialServerData.nbHits,
  term: initialServerData.term,
  max_price: initialServerData.max_price,
  min_price: initialServerData.min_price,
  facet_filters: initialServerData.facet_filters,
  facet_params: initialServerData.facet_params || {},
  facet_params_name: initialServerData.facet_params_name,
  parentFilterExists: false,
  showingLowestCategoryLevel: false,
  completeListOfFilters: [],
  categoryListOfFilters: [],
  categoryListWithCounts: [],
  categoryTargetFilter: {},
  selectedDropdown: CLOSE_MENU_PSEUDO_FACET_ID,
  isMobile: window.matchMedia
    ? window.matchMedia("(max-width: 767px)").matches
    : false,
  mobileFilterOpened: false,
  jobId: null,
  mobileFacetParams: initialServerData.facet_params || {},
  max_price_input: initialServerData.max_price,
  min_price_input: initialServerData.min_price,
  initialLocation: null,
  brand_params_name: initialServerData.brand_params_name,
});

const setStates = (state, newState) => {
  let updatedState = state;
  Object.keys(newState).forEach((key) => {
    updatedState = updatedState.set(key, newState[key]);
  });
  return updatedState;
};

const filterReducer = (state = initialState, action) => {
  switch (action.type) {
    case "SET":
      return setStates(state, action.payload);
    default:
      return state;
  }
};

export const history = createBrowserHistory();

const rootReducer = combineReducers({
  router: connectRouter(history),
  filterReducer,
});

const configureStore = (preloadedState) => {
  const store = createStore(
    rootReducer,
    preloadedState,
    compose(applyMiddleware(routerMiddleware(history), thunk))
  );
  return store;
};

export const store = configureStore();

const setFilterStates = (newState) => ({
  type: "SET",
  payload: newState,
});

const promisedSetFilterStates = (dispatch, newState) => {
  dispatch(setFilterStates(newState));
  return Promise.resolve();
};

export const processFilters = ({
  dataFromServer,
  reloadCategoryOnly,
  paramsForSlidingCategoryFilter,
  prioritizeCurrentJob,
  loadFiltersOnlyForMobile,
}) => (dispatch, getState) => {
  const currentState = getState().filterReducer;
  const currentJobId = currentState.get("jobId");

  if (!currentState.get("isMobile")) {
    utility.displayPriceRanges({ dataFromServer });
  }

  const stateHelpers = (() => {
    const finalStates = {
      showingLowestCategoryLevel: false,
      completeList: [],
      categoryList: [],
      categoryListWithCounts: null,
    };

    return {
      update: (state, value) => {
        finalStates[state] = value;
      },
      finalStates: () => finalStates,
    };
  })();

  // There are 3 pre-conditions we will use later
  // to decide whether to bring more data through ajax requests.
  // 1. if there's only category filter selected
  // AND for the category filter
  // 2. if one of the lowest level filters has been selected
  // 3. if parent filter exists
  const onlyCategoryFilterIsSelected = utility.checkIfOnlyCategoryFilterIsSelected(
    { filters: dataFromServer.facet_params }
  );

  let categoryUtilities = utility.setCategoryUtilities({
    filters: dataFromServer.facet_filters,
  });

  const originalCategoryListWithCounts = categoryUtilities.categoryList;

  let p0 = new Promise((resolve) => {
    resolve(null);
  });

  // Bring the complete list of filters
  // when multi-selectable filters are selected
  // with or without current_facet_id param
  // or when price has been selected
  if (
    !onlyCategoryFilterIsSelected ||
    utility.isPriceFilterSelected({ dataFromServer })
  ) {
    if (
      utility.isJobNotCurrent({
        jobId: dataFromServer.jobId,
        currentJobId,
      })
    ) {
      return;
    }

    // get complete list of filters
    p0 = api.bringCompleteListOfFilters({
      dataFromServer,
      categoryFilterId: dataFromServer.facet_params[CATEGORY_FACET_ID],
    });
  }

  p0.then((p0Data) => {
    let p1 = new Promise((resolve) => {
      resolve(null);
    });

    if (p0Data) {
      // set complete list of filters
      stateHelpers.update("completeList", p0Data.facet_filters);

      p0Data.facet_filters.forEach((filter) => {
        if (filter.facet.id === CATEGORY_FACET_ID) {
          // replace catgorylist
          stateHelpers.update(
            "categoryList",
            utility.findLowestChildren(filter.active_facet_tags)
          );
          // but keep the counts from the original categorylist
          stateHelpers.update(
            "categoryListWithCounts",
            originalCategoryListWithCounts
          );
        }
      });
    } else {
      stateHelpers.update("categoryList", categoryUtilities.categoryList);
    }

    categoryUtilities = utility.setCategoryUtilities({
      filters:
        stateHelpers.finalStates().completeList.length > 0
          ? stateHelpers.finalStates().completeList
          : dataFromServer.facet_filters,
    });

    // Replace the category list of filters with the list one level higher
    // when the lowest level category facet tag has been selected
    // and it is not denim depot case
    if (utility.isLowestCategoryLevelSelected({ categoryUtilities })) {
      if (
        utility.isJobNotCurrent({
          jobId: dataFromServer.jobId,
          currentJobId,
        })
      ) {
        return;
      }

      stateHelpers.update("showingLowestCategoryLevel", true);

      if (
        reloadCategoryOnly &&
        stateHelpers.finalStates().showingLowestCategoryLevel &&
        paramsForSlidingCategoryFilter
      ) {
        if (currentState.get("isMobile")) {
          const mobileFacetParams = Object.assign(
            {},
            currentState.get("mobileFacetParams")
          );
          mobileFacetParams[CATEGORY_FACET_ID] =
            paramsForSlidingCategoryFilter.facets[CATEGORY_FACET_ID];
          dispatch(setFilterStates({ mobileFacetParams }));
          dispatch(
            loadFiltersAndResults({
              params: paramsForSlidingCategoryFilter,
              loadFiltersOnlyForMobile: true,
            })
          );
          return;
        }

        dispatch(
          loadFiltersAndResults({ params: paramsForSlidingCategoryFilter })
        );
        return;
      }

      // get complete list of filters for one category level higher
      p1 = api.bringCompleteListOfFilters({
        dataFromServer,
        categoryFilterId: categoryUtilities.categoryTargetFilter.parent_id,
      });
    }

    p1.then((p1Data) => {
      let p2 = new Promise((resolve) => {
        resolve(null);
      });

      if (p1Data) {
        p1Data.facet_filters.forEach((filter) => {
          if (filter.facet.id === CATEGORY_FACET_ID) {
            stateHelpers.update(
              "categoryList",
              utility.findLowestChildren(filter.active_facet_tags)
            );
          }
        });

        p2 = api.bringCountData({
          dataFromServer,
          categoryFilterId: categoryUtilities.categoryTargetFilter.parent_id,
        });
      }

      p2.then((p2Data) => {
        if (p2Data) {
          p2Data.facet_filters.forEach((filter) => {
            if (filter.facet.id === CATEGORY_FACET_ID) {
              stateHelpers.update(
                "categoryListWithCounts",
                utility.findLowestChildren(filter.active_facet_tags)
              );
            }
          });
        }

        // replace counts of category list with category list with counts
        const finalCategoryList = utility.replaceCounts({
          list: stateHelpers.finalStates().categoryList,
          listWithCounts: stateHelpers.finalStates().categoryListWithCounts,
        });
        stateHelpers.update("categoryList", finalCategoryList);

        if (reloadCategoryOnly) {
          dispatch(
            setFilterStates({
              nbHits: dataFromServer.nbHits,
              categoryTargetFilter: categoryUtilities.categoryTargetFilter,
              categoryListOfFilters: stateHelpers.finalStates().categoryList,
              categoryListWithCounts: stateHelpers.finalStates()
                .categoryListWithCounts,
              parentFilterExists: categoryUtilities.categoryParentFilterExists,
              showingLowestCategoryLevel: stateHelpers.finalStates()
                .showingLowestCategoryLevel,
              jobId: prioritizeCurrentJob ? null : currentJobId,
            })
          );
        } else {
          dispatch(
            setFilterStates({
              nbHits: dataFromServer.nbHits,
              // in props, price might be null and Number(null) equals 0
              max_price: loadFiltersOnlyForMobile
                ? currentState.get("max_price")
                : Number(dataFromServer.max_price || NaN),
              min_price: loadFiltersOnlyForMobile
                ? currentState.get("min_price")
                : Number(dataFromServer.min_price || NaN),
              facet_filters:
                stateHelpers.finalStates().completeList.length > 0
                  ? stateHelpers.finalStates().completeList
                  : dataFromServer.facet_filters,
              facet_params: loadFiltersOnlyForMobile
                ? currentState.get("facet_params")
                : dataFromServer.facet_params || {},
              facet_params_name: dataFromServer.facet_params_name,
              categoryTargetFilter: categoryUtilities.categoryTargetFilter,
              showingLowestCategoryLevel: stateHelpers.finalStates()
                .showingLowestCategoryLevel,
              categoryListOfFilters: stateHelpers.finalStates().categoryList,
              categoryListWithCounts: stateHelpers.finalStates()
                .categoryListWithCounts,
              parentFilterExists: categoryUtilities.categoryParentFilterExists,
              isUltimateParentCategory: !categoryUtilities.categoryParentFilterExists,
              jobId: prioritizeCurrentJob ? null : currentJobId,
              mobileFacetParams: loadFiltersOnlyForMobile
                ? currentState.get("mobileFacetParams")
                : dataFromServer.facet_params || {},
              min_price_input: Number(dataFromServer.min_price || NaN),
              max_price_input: Number(dataFromServer.max_price || NaN),
              brand_params_name: dataFromServer.brand_params_name,
            })
          );
        }
      });
    });
  });
};

export const slideCategoryFilters = ({ params, resetCategory = false }) => (
  dispatch,
  getState
) => {
  // filter loading with new data
  promisedSetFilterStates(dispatch, { jobId: RESET_CATEGORY_JOB }).then(() => {
    const currentState = getState().filterReducer;
    params.jobId = RESET_CATEGORY_JOB;

    api
      .bringFilterData({
        params,
      })
      .then((filterData) => {
        if (
          utility.isJobNotCurrent({
            jobId: filterData.jobId,
            currentJobId: currentState.get("jobId"),
          })
        ) {
          return;
        }
        const paramsForSlidingCategoryFilter = resetCategory ? null : params;

        dispatch(
          processFilters({
            dataFromServer: filterData,
            reloadCategoryOnly: true,
            paramsForSlidingCategoryFilter,
          })
        );
      });
  });
};

export const showDropdown = (facetId) => (dispatch, getState) => {
  const currentState = getState().filterReducer;

  if (
    currentState.get("selectedDropdown") === CATEGORY_FACET_ID &&
    currentState.get("categoryTargetFilter").id !==
      Number(currentState.get("facet_params")[CATEGORY_FACET_ID])
  ) {
    const params = utility.constructParams({
      facets: Object.assign({}, currentState.get("facet_params")),
      minPrice: currentState.get("min_price"),
      maxPrice: currentState.get("max_price"),
      term: currentState.get("term"),
    });
    dispatch(slideCategoryFilters({ params, resetCategory: true }));
  }

  const priceFilterClosed =
    currentState.get("selectedDropdown") === PRICE_PSEUDO_FACET_ID &&
    currentState.get("selectedDropdown") === facetId;

  if (priceFilterClosed || facetId === CLOSE_MENU_PSEUDO_FACET_ID) {
    dispatch(
      setFilterStates({
        min_price_input: currentState.get("min_price"),
        max_price_input: currentState.get("max_price"),
      })
    );
  }

  if (currentState.get("selectedDropdown") === facetId) {
    dispatch(
      setFilterStates({
        selectedDropdown: CLOSE_MENU_PSEUDO_FACET_ID,
      })
    );
  } else {
    dispatch(
      setFilterStates({
        selectedDropdown: facetId,
      })
    );
  }
};

export const toggleSelectedFilter = (filter, brand) => (dispatch, getState) => {
  const currentFacetParamsName = getState().filterReducer.get(
    "facet_params_name"
  );
  const currentBrandParamsName = getState().filterReducer.get(
    "brand_params_name"
  );

  let facetParamsName = [];
  let brandParamsName = [];
  let filterAlreadySelected = false;

  if (filter) {
    for (let i = 0; i < currentFacetParamsName.length; i++) {
      const paramFilter = currentFacetParamsName[i];
      if (paramFilter !== undefined && paramFilter.id === filter.id) {
        filterAlreadySelected = true;
        break;
      }
    }
  }

  if (currentFacetParamsName && filterAlreadySelected) {
    currentFacetParamsName.forEach((paramFilter) => {
      if (
        filter !== undefined &&
        paramFilter !== undefined &&
        paramFilter.id !== filter.id
      ) {
        facetParamsName.push(paramFilter);
      }
    });
  } else {
    facetParamsName = currentFacetParamsName.slice();
    facetParamsName.push(filter);
  }

  if (currentBrandParamsName && filterAlreadySelected) {
    currentBrandParamsName.forEach((paramFilter) => {
      if (brand !== undefined && paramFilter.id !== brand.id) {
        brandParamsName.push(paramFilter);
      }
    });
  } else {
    brandParamsName = currentBrandParamsName.slice();
    brandParamsName.push(filter);
  }

  dispatch(
    setFilterStates({
      facet_params_name: facetParamsName,
      brand_params_name: brandParamsName,
    })
  );
};

export const pushState = (params, browserHistoryButtonClicked, initialLoad) => (
  dispatch
) => {
  if (!browserHistoryButtonClicked) {
    // change browser history and address bar
    const query = utility.pushState(params);

    if (initialLoad) {
      const initialLocation = {
        state: params,
        search: query,
      };

      promisedSetFilterStates(dispatch, { initialLocation }).then(() => {
        dispatch(
          replace({
            state: params,
            search: query,
          })
        );
      });
    } else {
      dispatch(
        push({
          state: params,
          search: query,
        })
      );
    }
  }
};

export const loadFiltersAndResults = ({
  params,
  reloadCategoryOnly,
  prioritizeCurrentJob,
  loadFiltersOnlyForMobile,
  browserHistoryButtonClicked,
}) => (dispatch, getState) => {
  const currentState = getState().filterReducer;
  const jobId =
    reloadCategoryOnly || prioritizeCurrentJob
      ? currentState.get("jobId")
      : null;

  params.jobId = jobId;

  if (!loadFiltersOnlyForMobile) {
    view.fadeResult();
    dispatch(pushState(params, browserHistoryButtonClicked));
  }

  // filter loading with new data
  api
    .bringFilterData({
      params,
    })
    .then((filterData) => {
      if (
        utility.isJobNotCurrent({
          jobId: filterData.jobId,
          currentJobId: currentState.get("jobId"),
        })
      ) {
        return;
      }

      if (loadFiltersOnlyForMobile) {
        dispatch(
          processFilters({
            dataFromServer: filterData,
            reloadCategoryOnly,
            prioritizeCurrentJob,
            loadFiltersOnlyForMobile,
          })
        );
        return;
      }

      dispatch(
        processFilters({
          dataFromServer: filterData,
          reloadCategoryOnly,
          prioritizeCurrentJob,
        })
      );

      // results loading with new data
      api
        .bringResultsData({
          params,
        })
        .then((resultsData) => {
          if (
            utility.isJobNotCurrent({
              jobId: filterData.jobId,
              currentJobId: currentState.get("jobId"),
            })
          ) {
            return;
          }

          view.loadResultsData({ resultsData });
          view.showResult();
          view.scrollToTop(currentState.get("isMobile"));
        });
    });
};

export const handleMultiSelectJob = (params) => (dispatch) => {
  const jobId = setTimeout(() => {}, 0);
  promisedSetFilterStates(dispatch, { jobId }).then(() => {
    dispatch(
      loadFiltersAndResults({
        params,
        reloadCategoryOnly: true,
      })
    );
  });
};

export const abortOngoingJobsAndPrioritizeCurrentJob = (params) => (
  dispatch
) => {
  // when removing a filter or clearing filters/price,
  // abort any ongoing jobs and prioritize the current job
  promisedSetFilterStates(dispatch, { jobId: PRIORITIZE_THIS_JOB }).then(() => {
    dispatch(
      loadFiltersAndResults({
        params,
        prioritizeCurrentJob: true,
      })
    );
  });
};

export const handlePriceFilterInput = (min, max) => (dispatch) => {
  // from the input, price might be '' and Number('') equals 0
  const min_price = Number(min === "" ? NaN : min);
  const max_price = Number(max === "" ? NaN : max);

  dispatch(
    setFilterStates({
      min_price_input: min_price,
      max_price_input: max_price,
    })
  );
};

export const handlePriceFilterClick = () => (dispatch, getState) => {
  const currentState = getState().filterReducer;
  const queryParameters = new URLSearchParams(location.search);

  const params = utility.constructParams({
    facets: Object.assign({}, currentState.get("facet_params")),
    minPrice: currentState.get("min_price_input"),
    maxPrice: currentState.get("max_price_input"),
    term: currentState.get("term"),
    brand_id: queryParameters.get("brand_id"),
  });
  dispatch(abortOngoingJobsAndPrioritizeCurrentJob(params));
};

export const handleCategoryFilterClick = (filter) => (dispatch, getState) => {
  const currentState = getState().filterReducer;
  let facets = {};
  const minPrice = currentState.get("min_price_input");
  const maxPrice = currentState.get("max_price_input");
  const term = currentState.get("term");
  let params = {};

  // if clicked filter is the original category filter
  // or the descendant of the original category filter
  // then apply all the params so it reflects the correct counts
  const clickedFilterIsDescendant =
    filter.ancestors[
      Number(currentState.get("facet_params")[CATEGORY_FACET_ID])
    ];
  if (clickedFilterIsDescendant) {
    filter.available_facets.forEach((facet) => {
      const facetTag = currentState.get("facet_params")[facet];
      if (facetTag) {
        facets[facet] = facetTag;
      }
    });
  }

  if (filter.id === null) {
    delete facets[CATEGORY_FACET_ID];
  } else {
    facets[CATEGORY_FACET_ID] = filter.id;
  }

  if (currentState.get("isMobile")) {
    const mobileFacetParams = Object.assign(
      {},
      currentState.get("mobileFacetParams")
    );

    if (filter.id === null) {
      delete mobileFacetParams[CATEGORY_FACET_ID];
    } else {
      mobileFacetParams[CATEGORY_FACET_ID] = filter.id;
    }

    params = utility.constructParams({
      facets,
      minPrice,
      maxPrice,
      term,
    });

    dispatch(setFilterStates({ mobileFacetParams }));
    dispatch(
      loadFiltersAndResults({
        params,
        loadFiltersOnlyForMobile: true,
      })
    );
  } else {
    params = utility.constructParams({
      facets,
      minPrice,
      maxPrice,
      term,
    });

    dispatch(loadFiltersAndResults({ params }));
  }
};

export const handleRemoveCategoryFilter = (filter) => (dispatch, getState) => {
  const currentState = getState().filterReducer;

  // if parent does not exist (meaning, the current filter is the ultimate parent)
  // don't do anything
  if (!filter.parent_id) {
    if (filter.fakeParentStoreExists) {
      const params = utility.constructParams({
        term: currentState.get("term"),
      });
      dispatch(toggleSelectedFilter(filter));
      dispatch(abortOngoingJobsAndPrioritizeCurrentJob(params));
    }
    return;
  }

  const facets = {};
  facets[CATEGORY_FACET_ID] = filter.parent_id;
  const params = utility.constructParams({
    facets,
    term: currentState.get("term"),
  });
  dispatch(toggleSelectedFilter(filter));
  dispatch(abortOngoingJobsAndPrioritizeCurrentJob(params));
};

export const clearAllFilters = () => (dispatch, getState) => {
  const currentState = getState().filterReducer;

  const params = utility.constructParams({
    term: currentState.get("term"),
  });
  dispatch(abortOngoingJobsAndPrioritizeCurrentJob(params));
};

export const clearPriceFilter = () => (dispatch, getState) => {
  const currentState = getState().filterReducer;

  let isPriceSet = false;
  for (let i = 0; i < currentState.get("facet_params_name").length; i++) {
    if (
      currentState.get("facet_params_name")[i].facet_id ===
      PRICE_PSEUDO_FACET_ID
    ) {
      isPriceSet = true;
      break;
    }
  }

  if (!isPriceSet) {
    dispatch(handlePriceFilterInput("", ""));
    return;
  }

  const params = utility.constructParams({
    facets: Object.assign({}, currentState.get("facet_params")),
    term: currentState.get("term"),
  });
  dispatch(abortOngoingJobsAndPrioritizeCurrentJob(params));
};

export const clearMultiSelectFilter = (filter) => (dispatch, getState) => {
  const currentState = getState().filterReducer;

  const facets = Object.assign({}, currentState.get("facet_params"));
  const minPrice = currentState.get("min_price");
  const maxPrice = currentState.get("max_price");
  const term = currentState.get("term");

  delete facets[filter.facet_id];

  const params = utility.constructParams({
    facets,
    minPrice,
    maxPrice,
    term,
  });

  dispatch(abortOngoingJobsAndPrioritizeCurrentJob(params));
};

export const handleFilterClick = ({ filter, brand, removeFilter }) => (
  dispatch,
  getState
) => {
  const currentState = getState().filterReducer;

  const facets = Object.assign({}, currentState.get("facet_params"));
  const minPrice = currentState.get("min_price");
  const maxPrice = currentState.get("max_price");
  const term = currentState.get("term");
  let brand_id = {};

  dispatch(toggleSelectedFilter(filter, brand));

  // If the facet of the clicked filter already exists
  if (filter) {
    if (facets[filter.facet_id]) {
      if (!Array.isArray(facets[filter.facet_id])) {
        facets[filter.facet_id] = [facets[filter.facet_id]];
      }

      const clickedFilterIndex = utility.findFilterIndex({
        facet: facets[filter.facet_id],
        filterId: filter.id,
      });

      // if the clicked filter does not exist in that facet,
      // then put the filter in the facet
      if (clickedFilterIndex === CLOSE_MENU_PSEUDO_FACET_ID) {
        facets[filter.facet_id].push(filter.id);
        // if the clicked filter already exists in that facet,
        // then remove that filter from the facet
        // and update current facet id
      } else {
        facets[filter.facet_id].splice(clickedFilterIndex, 1);
      }
      // If the facet of the clicked filter does not exist,
      // put the facet and the clicked filter into facets
    } else {
      facets[filter.facet_id] = [filter.id];
    }
  }

  // If the brand of the clicked filter already exists
  if (brand) {
    brand_id = brand.id;
  }

  const params = utility.constructParams({
    facets,
    minPrice,
    maxPrice,
    term,
    brand_id,
  });

  if (!removeFilter) {
    dispatch(
      setFilterStates({
        facet_params: facets,
        brand_params: brand_id,
      })
    );
    dispatch(handleMultiSelectJob(params));
    return;
  }
  dispatch(abortOngoingJobsAndPrioritizeCurrentJob(params));
};

export const setScreenSize = () => (dispatch, getState) => {
  const currentState = getState().filterReducer;
  const isMobileCurrently = currentState.get("isMobile");
  const isMobile = window.matchMedia("(max-width: 767px)").matches;

  if (!isMobileCurrently && isMobile) {
    dispatch(setFilterStates({ isMobile }));
    dispatch(showDropdown(CLOSE_MENU_PSEUDO_FACET_ID));
    view.toggleMobileFilter(false);
    view.turnOffDesktopFilter();
    return;
  }

  if (isMobileCurrently && !isMobile) {
    dispatch(setFilterStates({ isMobile }));
    dispatch(showDropdown(CLOSE_MENU_PSEUDO_FACET_ID));
    dispatch(toggleMobileFilter(false));
    view.turnOnDesktopFilter();
  }
};

export const handleClickListener = (action) => (dispatch, getState) => {
  const isMobile = window.matchMedia("(max-width: 767px)").matches;
  if (isMobile) {
    utility.takeActionWhenEventHappensOutsideOfSelector({
      event: "click",
      action,
      remove: true,
    });
  } else {
    utility.takeActionWhenEventHappensOutsideOfSelector({
      event: "click",
      action,
    });
  }
};

export const toggleMobileFilter = (applyFilters) => (dispatch, getState) => {
  const currentState = getState().filterReducer;
  const isMobile = currentState.get("isMobile");
  const newMobileFilterState = isMobile
    ? !currentState.get("mobileFilterOpened")
    : false;

  view.toggleMobileFilter(newMobileFilterState);
  promisedSetFilterStates(dispatch, {
    mobileFilterOpened: newMobileFilterState,
  }).then(() => {
    const currentState = getState().filterReducer;
    if (!currentState.get("mobileFilterOpened")) {
      if (applyFilters) {
        // when clicking done for mobile filter
        const params = utility.constructParams({
          facets: currentState.get("mobileFacetParams"),
          minPrice: currentState.get("min_price_input"),
          maxPrice: currentState.get("max_price_input"),
          term: currentState.get("term"),
        });
        dispatch(loadFiltersAndResults({ params }));
        view.removeFeaturedSales();
      } else {
        // when clicking cancel for mobile filter
        const params = utility.constructParams({
          facets: currentState.get("facet_params"),
          minPrice: currentState.get("min_price"),
          maxPrice: currentState.get("max_price"),
          term: currentState.get("term"),
        });

        dispatch(
          loadFiltersAndResults({ params, loadFiltersOnlyForMobile: true })
        );
        dispatch(
          setFilterStates({
            mobileFacetParams: currentState.get("facet_params"),
          })
        );
      }
    }
  });
};

export const mobileHandleFilterClick = (filter) => (dispatch, getState) => {
  const currentState = getState().filterReducer;
  const mobileFacetParams = Object.assign(
    {},
    currentState.get("mobileFacetParams")
  );

  // If the facet of the clicked filter alreday exists
  if (mobileFacetParams[filter.facet_id]) {
    if (!Array.isArray(mobileFacetParams[filter.facet_id])) {
      mobileFacetParams[filter.facet_id] = [mobileFacetParams[filter.facet_id]];
    }

    const clickedFilterIndex = utility.findFilterIndex({
      facet: mobileFacetParams[filter.facet_id],
      filterId: filter.id,
    });

    // if the clicked filter does not exist in that facet,
    // then put the filter in the facet
    if (clickedFilterIndex === CLOSE_MENU_PSEUDO_FACET_ID) {
      mobileFacetParams[filter.facet_id].push(filter.id);
      // if the clicked filter already exists in that facet,
      // then remove that filter from the facet
      // and update current facet id
    } else {
      mobileFacetParams[filter.facet_id].splice(clickedFilterIndex, 1);
    }
    // If the facet of the clicked filter does not exist,
    // put the facet and the clicked filter into mobileFacetParams
  } else {
    mobileFacetParams[filter.facet_id] = [filter.id];
  }
  dispatch(setFilterStates({ mobileFacetParams }));
};

export const handleQuery = (params) => (dispatch, getState) => {
  if (!params || typeof params !== "object") {
    const error = "Error: handleQuery on pop: Params is not an object";
    Rollbar.error(error, { params });
    return error;
  }

  dispatch(
    loadFiltersAndResults({
      params,
      browserHistoryButtonClicked: true,
    })
  );
};

export const expandFilter = () => () => adjustStickyFilter(true);
